<template>
  <div class="timeline-footer">
    <!-- Left: Author and Associations -->
    <div class="flex flex-wrap items-center gap-2 footer-left">
      <!-- Author -->
      <span class="footer-pill">
        <Icon
          :name="authorIcon"
          size="14"
        />
        <span>{{ authorName }}</span>
      </span>

      <!-- Contact -->
      <a
        v-if="contact"
        :href="`/contacts/${contact.id}/activity`"
        target="_blank"
        rel="noopener noreferrer"
        class="footer-pill-link"
      >
        <Icon
          name="mdi:account-outline"
          size="14"
        />
        <span>{{ contact.firstName }} {{ contact.lastName }}</span>
      </a>

      <!-- Business -->
      <a
        v-if="business"
        :href="`/businesses/${business.id}/details/profile`"
        target="_blank"
        rel="noopener noreferrer"
        class="footer-pill-link"
      >
        <Icon
          :name="SidebarIcon.BUSINESS"
          size="14"
        />
        <span>{{ business.name }}</span>
      </a>

      <!-- Contract -->
      <a
        v-if="contract"
        :href="`/contracts/${contract.id}/details`"
        target="_blank"
        rel="noopener noreferrer"
        class="footer-pill-link"
      >
        <Icon
          name="hugeicons:contracts"
          size="14"
        />
        <span>{{ contract.name }}</span>
      </a>
    </div>

    <!-- Right: Time -->
    <div class="footer-time-pill">
      <Icon
        name="mdi:clock-time-four"
        size="14"
      />
      <span>{{ formatTimeAgo(createdAt) }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { SidebarIcon } from '~/types/baseIcon'

const { useAgo } = useUtils()

interface Props {
  authorName: string
  authorIcon?: string
  contact?: {
    id: string
    firstName: string
    lastName: string
  }
  business?: {
    id: string
    name: string
  }
  contract?: {
    id: string
    name: string
  }
  createdAt: Date | string
}

const props = withDefaults(defineProps<Props>(), {
  authorIcon: 'mdi:account-circle-outline',
})

const formatTimeAgo = (date: Date | string) => {
  if (!date) return ''
  const dateStr = typeof date === 'string' ? date : date.toISOString()
  return useAgo(dateStr)
}
</script>

<style scoped>
.timeline-footer {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(241, 245, 249, 1);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.footer-left {
  font-size: 0.72rem;
}

.footer-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 650;
  color: #0f172a;
  background: rgba(248, 250, 252, 0.85);
  border: 1px solid rgba(226, 232, 240, 0.85);
}

.footer-pill-link {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #475569;
  background: rgba(248, 250, 252, 0.85);
  border: 1px solid rgba(226, 232, 240, 0.85);
  text-decoration: none;
  transition: all 160ms ease;
}

.footer-pill-link:hover {
  color: #0f172a;
  background: rgba(241, 245, 249, 0.95);
  border-color: rgba(148, 163, 184, 0.7);
  text-decoration: none;
  transform: translateY(-1px);
}

.footer-time-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 650;
  color: #64748b;
  background: rgba(248, 250, 252, 0.85);
  border: 1px solid rgba(226, 232, 240, 0.85);
}

/* Responsive */
@media (max-width: 640px) {
  .footer-left {
    font-size: 0.625rem;
  }

  .footer-pill,
  .footer-pill-link,
  .footer-time-pill {
    font-size: 0.625rem;
    padding: 0.2rem 0.45rem;
  }

  .timeline-footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
